import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Upload, Icon, Message } from '@b-design/fusion';
import Button, { promisifyValidate } from '@/components/Button';
import { Field } from '@alifd/next';
import $i18n from '../../../../../i18n';
import Requests from '@/services/request';
import { getQueryStringArgs } from '@/utils/url-parse';
import { versionObj } from '@/utils/handleVersionStr';

import './index.less';

interface Props {
  fileVisible: boolean;
  setFileVisible: any;
  setFlag: any;
  info: any;
  maxVersion: any;
}

const FileUploadModal: React.FC<Props> = ({
  fileVisible,
  setFileVisible,
  setFlag,
  info,
  maxVersion,
}) => {
  const { appId, spaceId } = getQueryStringArgs();
  const [url, setUrl] = useState('');
  const field = Field.useField();
  const { init, getValue, setValue, getError } = field;
  // 是否文件上传中
  const [isUploading, setIsUploading] = useState<boolean>(false)

  useEffect(() => {
    setValue('version', versionObj(maxVersion).versionAddOne())
  }, [maxVersion])


  useEffect(() => {
    if (fileVisible) {
      setUrl('');
    }
  }, [fileVisible]);

  useEffect(() => {
    setUrl('');
  }, [getValue('version')]);

  const customRequest = async (option) => {
    const [errs, vals] = await promisifyValidate(field);
      if (errs) {
        return;
      }

      setIsUploading(true);

      const maxSizeFile = 100 * 1024 * 1024;
      const currentFileSize = option.file.size;
      if (currentFileSize > maxSizeFile) {
        Message.error({
          title: $i18n.get({
            id: 'aopf.pages.miniapp.versions.File.Upload.Upload.failed.File.volume.should.not.exceed',
            dm: '上传失败,文件大小不能超过100M。',
          }),
        });

        return;
      }

     const data = await Requests.post(
        '/rest/intercnn/app/version/uploadResource',
        {
          spaceId,
          appId,
          version: getValue('version'),
          file: option.file,
        },
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      setUrl(data.relativePath);
      setIsUploading(false);
      Message.success({
        title: $i18n.get({
          id: 'aopf.pages.miniapp.versions.File.Upload.Upload.successfully',
          dm: '上传成功',
        }),
      });
  };

  const submit = async () => {
    const [errs, vals] = await promisifyValidate(field)
      if (errs) {
        return;
      }

      if (url) {
        const res = await Requests.post('/rest/intercnn/app/version/create', {
          appId,
          artifactType: 'FILE',
          artifactList: [url],
          name: getValue('version'),
          source: 'UPLOAD',
          version: getValue('version'),
        });
         if (res) {
            Message.success({
              title: $i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.Created.successfully',
                dm: '创建成功',
              }),
            });
            setFileVisible(false);
            setFlag(true);
          }
      } else {
        Message.error({
          title: $i18n.get({
            id: 'aopf.pages.miniapp.versions.File.Upload.Please.upload.the.file',
            dm: '请上传文件',
          }),
        });
      }
  };

  const versionValidator = (_rules, value, callback) => {
    // 当前应用无最新线上版本时无需校验
    if (!maxVersion) {
      callback();
      return;
    }
    if (!versionObj(value).isLaterThan(maxVersion)) {
      callback(
        $i18n.get(
          {
            id: 'aopf.pages.miniapp.versions.table.menubutton.modal.version.msg',
            dm: '版本号需高于当前最新版本: {maxVersion}',
          },
          { maxVersion: maxVersion },
        ),
      );
    } else {
      callback();
    }
  };

  return (
    <div className="modal-file-upload-container">
      <Modal
        title={$i18n.get({
          id: 'aopf.pages.miniapp.versions.table.menubutton.modal.Upload.By.Files',
          dm: '文件上传',
        })}
        data-size="small"
        visible={fileVisible}
        onOk={submit}
        onCancel={() => setFileVisible(false)}
        onClose={() => setFileVisible(false)}
        footerActions={['cancel', 'ok']}
        okProps={{
          loading: isUploading,
          disabled: isUploading,
        }}
        footer={[
          <div className="dialog-footer">
            <Button className="dialog-btn" type="normal" onClick={() => setFileVisible(false)}>
              {$i18n.get({ id: 'aopf.component.invite.Cancel', dm: '取消' })}
            </Button>
            <Button type="primary" onClick={() => submit()} disabled={isUploading} loading={isUploading}>
              {$i18n.get({ id: 'aopf.component.invite.Ok', dm: '确定' })}
            </Button>
          </div>,
        ]}
      >
        <Form style={{ maxWidth: '350px', paddingLeft: '30px' }}>
          <Form.Item
            name="version"
            label={$i18n.get({
              id: 'aopf.pages.miniapp.versions.table.Action.Distribute.Channel.modal.Version',
              dm: '版本',
            })}
            required
          >
            <Input
              placeholder={$i18n.get({
                id: 'aopf.pages.miniapp.versions.table.Action.Distribute.Channel.modal.Please.enter.the.version',
                dm: '请输入版本',
              })}
              {...init('version', {
                initValue: versionObj(maxVersion).versionAddOne(),
                rules: [
                  {
                    required: 'true',
                    message: $i18n.get({
                      id: 'aopf.pages.miniapp.versions.table.Action.Distribute.Channel.modal.Please.enter.the.version',
                      dm: '请输入版本',
                    }),
                  },
                  {
                    pattern: /[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}/,
                    message: $i18n.get({
                      id: 'aopf.pages.miniapp.versions.table.menubutton.modal.Naming.convention',
                      dm: '命名约定: 1.0.0',
                    }),
                    trigger: 'onChange',
                  },
                  {
                    validator: versionValidator,
                    trigger: ['onChange'],
                  },
                ],
              })}
            />
            <p style={{ fontSize: 12, color: '#c82727' }}>{getError('version')}</p>
          </Form.Item>
          <Form.Item
            name="file"
            label={$i18n.get({
              id: 'aopf.pages.miniapp.versions.table.Action.Distribute.Channel.modal.Upload.Product',
              dm: '上传应用文件',
            })}
            required
            requiredMessage={$i18n.get({
              id: 'aopf.pages.miniapp.versions.table.menubutton.modal.Upload.miniapp.file.with.the.extension.zip.for.Windvane.and.wgt.for.Uniapp',
              dm: '上传miniapp文件,Windvane扩展名为.zip,Uniapp扩展名为.wgt。',
            })}
          >
            <Upload
              accept={info.framework === 'WindVane' ? '.zip' : '.wgt'}
              data-size="medium"
              listType="text"
              style={{ width: '100%' }}
              request={customRequest}
              limit={1}
              onCancel={() => {
                setUrl('');
              }}
              disabled={getError('version') || !getValue('version')}
            >
              <Button
                type="normal"
                data-ctype="icon-text"
                style={{ color: '#474A52', width: '100%', justifyContent: 'flex-start' }}
                size="medium"
              >
                <Icon type="upload" style={{ color: 'var(--superapp-primary-1)' }} />
                <span className="upload-btn-font">
                  {$i18n.get({
                    id: 'aopf.pages.miniapp.versions.table.Action.Distribute.Channel.modal.Upload.local.files',
                    dm: '上传本地文件',
                  })}
                </span>
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FileUploadModal;
